import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { isNativeApp } from '../lib/native';
import { useBranding } from '../contexts/BrandingContext';
import { Users, Plus, Trash2, Building2, Truck, ArrowRight, DollarSign, BarChart3, CreditCard, Wrench, Sparkles, X, Pencil, ShieldAlert, ShieldCheck } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import SyncHealthWidget from '../components/SyncHealthWidget';
import DriverHeartbeatMap from '../components/DriverHeartbeatMap';
import ContractExpiryPanel from '../components/ContractExpiryPanel';
import WelcomeChecklist from '../components/WelcomeChecklist';
import { useNavigate, Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NUDGE_DISMISS_KEY = 'civilx.bizDetailsNudge.dismissed';

const CompanyAdminDashboard = () => {
  const { user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [compliance, setCompliance] = useState({ expired: 0, soon: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', password: '', name: '', role: 'dispatcher' });
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try { return localStorage.getItem(NUDGE_DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Fields that the paystub + invoice PDFs read from Company Branding.
  // The nudge only fires while at least one is empty AND the user hasn't
  // explicitly dismissed it for this browser.
  const businessDetailsIncomplete = useMemo(() => (
    !branding?.address_line1 || !branding?.city || !branding?.state ||
    !branding?.zip_code || !branding?.phone
  ), [branding]);

  const dismissNudge = () => {
    try { localStorage.setItem(NUDGE_DISMISS_KEY, '1'); } catch { /* private mode */ }
    setNudgeDismissed(true);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, driversRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/drivers`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
    try {
      const c = await axios.get(`${API}/assets/compliance/summary`, { withCredentials: true });
      setCompliance(c.data);
    } catch { /* non-fatal */ }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, { ...userForm, company_id: user.company_id }, { withCredentials: true });
      setUserForm({ email: '', password: '', name: '', role: 'dispatcher' });
      setShowUserForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-8">Loading...</div></DashboardLayout>;
  }

  const dispatchers = users.filter(u => u.role === 'dispatcher');
  const teamMembers = users.filter(u => u.role === 'dispatcher' || u.role === 'company_admin');
  const activeDrivers = drivers.filter(d => d.status === 'active');
  const pendingDrivers = drivers.filter(d => d.status === 'pending_approval');

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {branding.welcome_message && branding.welcome_message !== 'Welcome back'
              ? branding.welcome_message
              : `Welcome back, ${user?.name?.split(' ')[0] || 'Admin'}`}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {branding.company_name ? `${branding.company_name} — ` : ''}Manage your dispatchers, drivers and operations.
            </p>
            {compliance.carrier && !compliance.carrier.out_of_service && compliance.carrier.insurance_ok !== false && (
              <span
                data-testid="fmcsa-verified-badge"
                title={`DOT #${compliance.carrier.dot_number} · ${compliance.carrier.operating_status}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                FMCSA Verified · Authorized
              </span>
            )}
          </div>
        </div>

        {businessDetailsIncomplete && !nudgeDismissed && (
          <div
            data-testid="business-details-nudge"
            className="mb-8 relative overflow-hidden bg-gradient-to-r from-[#002FA7] to-[#1e3a8a] text-white rounded-md p-5 sm:p-6 shadow-sm"
          >
            <button
              data-testid="nudge-dismiss"
              onClick={dismissNudge}
              aria-label="Dismiss"
              className="absolute top-3 right-3 p-1 rounded hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4 text-white/70" />
            </button>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pr-8">
              <div className="w-11 h-11 rounded-md bg-white/15 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="text-base font-bold tracking-tight" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  Finish setting up your company profile
                </p>
                <p className="text-sm text-white/80 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Add your business address &amp; phone so every invoice and paystub goes out looking professional. Takes about 30 seconds.
                </p>
              </div>
              <Link
                to="/company/branding"
                data-testid="nudge-cta"
                className="self-start sm:self-auto inline-flex items-center gap-2 bg-white text-[#002FA7] hover:bg-slate-100 font-semibold text-sm rounded-md px-4 py-2 whitespace-nowrap transition-colors"
              >
                Set it up <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        <WelcomeChecklist />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div data-testid="stat-card-total-users" className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 text-[#002FA7]" strokeWidth={2} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Total Staff</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{users.length}</p>
              </div>
            </div>
          </div>

          <div data-testid="stat-card-dispatchers" className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-8 h-8 text-[#34C759]" strokeWidth={2} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Dispatchers</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{dispatchers.length}</p>
              </div>
            </div>
          </div>

          <div data-testid="stat-card-active-drivers" className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <Truck className="w-8 h-8 text-[#002FA7]" strokeWidth={2} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Active Drivers</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{activeDrivers.length}</p>
              </div>
            </div>
          </div>

          <div data-testid="stat-card-pending-drivers" className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 text-[#FF9500]" strokeWidth={2} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Pending Approval</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{pendingDrivers.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6 text-[#002FA7]" strokeWidth={2} />
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Drivers</h2>
                  <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Invite & approve</p>
                </div>
              </div>
              <button
                data-testid="manage-drivers-button"
                onClick={() => navigate('/drivers')}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Open
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6 text-[#34C759]" strokeWidth={2} />
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Loads</h2>
                  <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Create & assign</p>
                </div>
              </div>
              <button
                data-testid="manage-loads-button"
                onClick={() => navigate('/loads')}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Open
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-[#FF9500]" strokeWidth={2} />
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Payroll</h2>
                  <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Weekly runs</p>
                </div>
              </div>
              <button
                data-testid="manage-payroll-button"
                onClick={() => navigate('/payroll')}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Open
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-[#002FA7]" strokeWidth={2} />
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Analytics</h2>
                  <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>KPIs & trends</p>
                </div>
              </div>
              <button
                data-testid="view-analytics-button"
                onClick={() => navigate('/analytics')}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Open
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {!isNativeApp() && (
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-[#34C759]" strokeWidth={2} />
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Billing</h2>
                  <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Plans & subscription</p>
                </div>
              </div>
              <button
                data-testid="view-billing-button"
                onClick={() => navigate('/billing')}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Open
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Wrench className="w-6 h-6 text-[#FF9500]" strokeWidth={2} />
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Assets</h2>
                  <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trucks, trailers & devices</p>
                </div>
              </div>
              <button
                data-testid="manage-assets-button"
                onClick={() => navigate('/assets')}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Open
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <SyncHealthWidget />
          <DriverHeartbeatMap />
        </div>

        {/* FMCSA carrier Out-of-Service alert */}
        {compliance.carrier?.out_of_service && (
          <div data-testid="carrier-oos-alert" className="bg-red-50 border border-red-300 rounded-md p-5 mb-6 flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex-1">
              <h3 className="text-base font-bold text-red-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>Carrier Out of Service</h3>
              <p className="text-sm text-red-700 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                FMCSA reports <strong>{compliance.carrier.legal_name}</strong>
                {compliance.carrier.dot_number ? ` (DOT #${compliance.carrier.dot_number})` : ''} as
                {' '}"<strong>{compliance.carrier.operating_status}</strong>". Operating authority may be inactive — resolve with FMCSA to avoid interruptions.
              </p>
              <button
                data-testid="carrier-oos-review"
                onClick={() => navigate('/admin/settings')}
                className="mt-2 text-sm font-semibold text-red-700 hover:underline"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Review carrier status →
              </button>
            </div>
          </div>
        )}

        {/* FMCSA insurance issue alert */}
        <ContractExpiryPanel />
        {compliance.carrier && compliance.carrier.insurance_ok === false && (
          <div data-testid="carrier-insurance-alert" className="bg-amber-50 border border-amber-300 rounded-md p-5 mb-6 flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex-1">
              <h3 className="text-base font-bold text-amber-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>FMCSA insurance attention needed</h3>
              <ul className="text-sm text-amber-700 mt-1 list-disc list-inside" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {(compliance.carrier.insurance_issues || []).map((i, idx) => <li key={idx}>{i}</li>)}
              </ul>
              <button
                data-testid="carrier-insurance-review"
                onClick={() => navigate('/admin/settings')}
                className="mt-2 text-sm font-semibold text-amber-700 hover:underline"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Review carrier status →
              </button>
            </div>
          </div>
        )}

        {/* Fleet Compliance Board */}
        {(compliance.expired > 0 || compliance.soon > 0) && (
          <div data-testid="fleet-compliance-board" className="bg-white border border-slate-200 rounded-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Fleet Compliance</h2>
              <div className="flex items-center gap-2">
                {compliance.expired > 0 && <span className="px-2.5 py-1 rounded text-xs font-bold bg-red-100 text-red-700">{compliance.expired} expired</span>}
                {compliance.soon > 0 && <span className="px-2.5 py-1 rounded text-xs font-bold bg-amber-100 text-amber-800">{compliance.soon} expiring</span>}
                <button
                  data-testid="compliance-export-csv"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/assets/compliance/export`, { withCredentials: true, responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                      const a = document.createElement('a');
                      a.href = url; a.download = 'compliance.csv';
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch { /* ignore */ }
                  }}
                  className="px-2.5 py-1 rounded text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Export CSV
                </button>
                <button
                  data-testid="compliance-export-pdf"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/assets/compliance/export-pdf`, { withCredentials: true, responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      a.href = url; a.download = 'compliance.pdf';
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch { /* ignore */ }
                  }}
                  className="px-2.5 py-1 rounded text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  PDF
                </button>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {compliance.items.slice(0, 6).map((it) => (
                <button
                  key={it.id}
                  data-testid={`compliance-item-${it.id}`}
                  onClick={() => navigate(`/assets/${it.id}`)}
                  className="w-full flex items-center justify-between py-2.5 text-left hover:bg-slate-50 rounded px-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {it.asset_number ? `#${it.asset_number} · ` : ''}{it.label}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {it.flags.map((f) => `${f.label} ${f.level === 'expired' ? 'expired' : `in ${f.days}d`}`).join(' · ')}
                    </p>
                  </div>
                  <span className={`ml-3 flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${it.worst === 'expired' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                    {it.worst}
                  </span>
                </button>
              ))}
            </div>
            {compliance.items.length > 6 && (
              <button onClick={() => navigate('/assets')} className="mt-3 text-sm font-semibold text-[#002FA7] hover:underline" data-testid="compliance-view-all">
                View all {compliance.items.length} in Asset Management →
              </button>
            )}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Team members</h2>
              <p className="text-sm text-slate-600 mt-0.5">Dispatchers &amp; company admins.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="view-all-team-button"
                onClick={() => navigate('/team')}
                className="text-sm text-[#002FA7] hover:text-[#00247D] font-semibold"
              >
                View all →
              </button>
              <button
                data-testid="add-user-button"
                onClick={() => setShowUserForm(!showUserForm)}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Add member
              </button>
            </div>
          </div>

          {showUserForm && (
            <form onSubmit={handleCreateUser} className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Name"
                  data-testid="user-name-input"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  data-testid="user-email-input"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  data-testid="user-password-input"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  required
                />
                <select
                  data-testid="user-role-select"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <option value="dispatcher">Dispatcher</option>
                  <option value="company_admin">Company Admin</option>
                </select>
              </div>
              <button
                type="submit"
                data-testid="submit-user-button"
                className="bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Create user
              </button>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Name</th>
                  <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Email</th>
                  <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Role</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.slice(0, 5).map((u) => {
                  const isSelf = u.id === user?.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {u.name}{isSelf && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{u.email}</td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${u.role === 'company_admin' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-emerald-100 text-emerald-700'}`}>
                          {u.role === 'company_admin' ? 'Company Admin' : 'Dispatcher'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right space-x-3">
                        <button
                          data-testid={`edit-user-${u.id}`}
                          onClick={() => navigate('/team')}
                          className="text-[#002FA7] hover:text-[#00247D] transition-colors"
                          title="Edit on Team page"
                        >
                          <Pencil className="w-4 h-4 inline" strokeWidth={2} />
                        </button>
                        {!isSelf && (
                          <button
                            data-testid={`delete-user-${u.id}`}
                            onClick={() => handleDeleteUser(u.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 inline" strokeWidth={2} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {teamMembers.length > 5 && (
              <p className="text-center text-xs text-slate-500 mt-4">
                Showing first 5 of {teamMembers.length}.{' '}
                <button onClick={() => navigate('/team')} className="text-[#002FA7] font-semibold hover:underline">
                  View all →
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyAdminDashboard;